import { a as focusRings, c as getFirstFocusable, d as colors, i as focusOutlines, l as getFocusableElements, n as focusColors, o as focusVisibleStyles, r as focusIndicatorStyles, s as focusableSelectors, t as componentFocus, u as getLastFocusable } from "../focus-DaIphPOd.mjs";
import { _ as lineHeights, a as getTransition, c as transitions, d as spacing, f as zIndices, g as letterSpacings, h as fonts, i as getAnimation, l as radii, m as fontWeights, n as durations, o as keyframes, p as fontSizes, r as easings, s as reducedMotionQuery, t as animations, u as shadows, v as typography } from "../motion-Cg-JnSSP.mjs";
//#region tokens/index.ts
/**
* Raw token objects for external tooling (Style Dictionary, Figma export, etc.)
* Use this when you need to programmatically access all token values
*/
const rawTokens = {
	colors,
	fonts,
	fontSizes,
	fontWeights,
	lineHeights,
	letterSpacings,
	spacing,
	radii,
	shadows,
	zIndices,
	durations,
	easings,
	focusRings,
	focusColors
};
/**
* All tokens as a single object for convenience
*/
const tokens = {
	colors,
	fonts,
	fontSizes,
	fontWeights,
	spacing,
	radii,
	shadows,
	transitions,
	zIndices,
	durations,
	easings,
	animations,
	keyframes,
	focusRings,
	focusColors,
	componentFocus
};
/**
* CSS Custom Properties
* Use this string in a <style> tag or CSS file
*/
const cssVariables = `
:root {
  /* Colors - Void */
  --e-void: ${colors.void};
  --e-void-light: ${colors.voidLight};
  --e-void-lighter: ${colors.voidLighter};
  
  /* Colors - Surface */
  --e-surface: ${colors.surface};
  --e-surface-elevated: ${colors.surfaceElevated};
  --e-surface-overlay: ${colors.surfaceOverlay};
  
  /* Colors - Mint (Primary) */
  --e-mint: ${colors.mint};
  --e-mint-dim: ${colors.mintDim};
  --e-mint-glow: ${colors.mintGlow};
  --e-mint-glow-strong: ${colors.mintGlowStrong};
  
  /* Colors - Semantic */
  --e-warning: ${colors.warning};
  --e-warning-glow: ${colors.warningGlow};
  --e-danger: ${colors.danger};
  --e-danger-glow: ${colors.dangerGlow};
  --e-info: ${colors.info};
  --e-info-glow: ${colors.infoGlow};
  --e-sub: ${colors.sub};
  --e-sub-glow: ${colors.subGlow};
  
  /* Colors - Text */
  --e-text-primary: ${colors.textPrimary};
  --e-text-secondary: ${colors.textSecondary};
  --e-text-muted: ${colors.textMuted};
  
  /* Colors - Borders */
  --e-border: ${colors.border};
  --e-border-hover: ${colors.borderHover};
  --e-border-active: ${colors.borderActive};
  
  /* Colors - Glass */
  --e-glass-bg: ${colors.glassBg};
  --e-glass-border: ${colors.glassBorder};
  
  /* Typography */
  --e-font-mono: ${fonts.mono};
  --e-font-sans: ${fonts.sans};
  
  /* Radii */
  --e-radius-none: ${radii.none};
  --e-radius-sm: ${radii.sm};
  --e-radius-md: ${radii.md};
  --e-radius-lg: ${radii.lg};
  --e-radius-xl: ${radii.xl};
  --e-radius-2xl: ${radii["2xl"]};
  --e-radius-full: ${radii.full};
  
  /* Shadows */
  --e-shadow-sm: ${shadows.sm};
  --e-shadow-md: ${shadows.md};
  --e-shadow-lg: ${shadows.lg};
  --e-shadow-glow: ${shadows.glow};
  --e-shadow-glow-strong: ${shadows.glowStrong};
  --e-shadow-mint-glow: ${shadows.mintGlow};
  --e-shadow-focus-ring: ${shadows.focusRing};
  --e-shadow-glass: ${shadows.glass};
  
  /* Transitions */
  --e-transition-fast: ${transitions.fast};
  --e-transition-normal: ${transitions.normal};
  --e-transition-slow: ${transitions.slow};
}
`;
/**
* Tailwind CSS config extension
* Use with: theme: { extend: tailwindTheme }
*/
const tailwindTheme = {
	colors: {
		void: {
			DEFAULT: colors.void,
			light: colors.voidLight,
			lighter: colors.voidLighter
		},
		surface: {
			DEFAULT: colors.surface,
			elevated: colors.surfaceElevated,
			overlay: colors.surfaceOverlay
		},
		mint: {
			DEFAULT: colors.mint,
			dim: colors.mintDim,
			glow: colors.mintGlow,
			"glow-strong": colors.mintGlowStrong
		},
		warning: {
			DEFAULT: colors.warning,
			glow: colors.warningGlow
		},
		danger: {
			DEFAULT: colors.danger,
			glow: colors.dangerGlow
		},
		info: {
			DEFAULT: colors.info,
			glow: colors.infoGlow
		},
		sub: {
			DEFAULT: colors.sub,
			glow: colors.subGlow
		},
		text: {
			primary: colors.textPrimary,
			secondary: colors.textSecondary,
			muted: colors.textMuted
		},
		border: {
			DEFAULT: colors.border,
			hover: colors.borderHover,
			active: colors.borderActive
		},
		glass: {
			bg: colors.glassBg,
			border: colors.glassBorder
		}
	},
	fontFamily: {
		mono: [fonts.mono],
		sans: [fonts.sans]
	},
	borderRadius: {
		none: radii.none,
		sm: radii.sm,
		md: radii.md,
		lg: radii.lg,
		xl: radii.xl,
		"2xl": radii["2xl"],
		full: radii.full,
		button: radii.button,
		input: radii.input,
		card: radii.card,
		panel: radii.panel,
		modal: radii.modal,
		badge: radii.badge,
		avatar: radii.avatar
	},
	boxShadow: {
		none: shadows.none,
		sm: shadows.sm,
		md: shadows.md,
		lg: shadows.lg,
		glow: shadows.glow,
		"glow-strong": shadows.glowStrong,
		"mint-glow": shadows.mintGlow,
		"mint-glow-strong": shadows.mintGlowStrong,
		"glow-danger": shadows.glowDanger,
		"glow-warning": shadows.glowWarning,
		"glow-info": shadows.glowInfo,
		"focus-ring": shadows.focusRing,
		"focus-ring-strong": shadows.focusRingStrong,
		glass: shadows.glass
	},
	transitionDuration: {
		fast: "100ms",
		normal: "150ms",
		slow: "300ms"
	},
	zIndex: zIndices
};
const breakpoints = {
	sm: 640,
	md: 768,
	lg: 1024
};
const mediaQuery = {
	sm: `@media (max-width: ${breakpoints.sm}px)`,
	md: `@media (max-width: ${breakpoints.md}px)`,
	lg: `@media (max-width: ${breakpoints.lg}px)`
};
//#endregion
export { animations, breakpoints, colors, componentFocus, cssVariables, durations, easings, focusColors, focusIndicatorStyles, focusOutlines, focusRings, focusVisibleStyles, focusableSelectors, fontSizes, fontWeights, fonts, getAnimation, getFirstFocusable, getFocusableElements, getLastFocusable, getTransition, keyframes, letterSpacings, lineHeights, mediaQuery, radii, rawTokens, reducedMotionQuery, shadows, spacing, tailwindTheme, tokens, transitions, typography, zIndices };

//# sourceMappingURL=index.mjs.map